import { useEffect, useState } from "react";
import { Heart, RefreshCw, ExternalLink, Calendar, Clock, Users, FileText, Plus, Shield, Activity } from "lucide-react";
import { contractService } from "../services/contractService";
import { useWallet } from "../Context/WalletContext";
import { handleError } from "../utils/errorHandler";
import ModernNavbar from "./ModernNavbar";

export default function ModernDashboard({ onRegisterClick }) {
  const [policies, setPolicies] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const { address } = useWallet();

  // Load user's policies from blockchain
  const loadUserPolicies = async (userAddr) => {
    try {
      setLoading(true);
      setStatus("Loading your policies...");
      const policyIds = await contractService.getUserPolicies(userAddr);
      
      const policyPromises = policyIds.map(async (policyId) => {
        try {
          const policyData = await contractService.getPolicyView(policyId);
          return {
            policyId,
            ...policyData,
            lastHeartbeat: Number(policyData.lastHeartbeat),
            heartbeatInterval: Number(policyData.heartbeatInterval),
            requiredQuorum: Number(policyData.requiredQuorum),
            status: Number(policyData.status),
            createdAt: Number(policyData.createdAt)
          };
        } catch (error) {
          console.error(`Error loading policy ${policyId}:`, error);
          return null;
        }
      });
      
      const policiesData = await Promise.all(policyPromises);
      const validPolicies = policiesData.filter(p => p !== null);
      
      setPolicies(validPolicies);
      setStatus("");
    } catch (error) {
      const errorInfo = handleError(error, 'Load Policies');
      setStatus(`Error: ${errorInfo.userMessage}`);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (address) {
      loadUserPolicies(address);
    }
  }, [address]);

  // Send Heartbeat
  const sendHeartbeat = async (policyId, index) => {
    try {
      setStatus("Sending heartbeat...");
      await contractService.heartbeat(policyId);
      
      // Update the policy in the list
      const updatedPolicies = [...policies];
      updatedPolicies[index].lastHeartbeat = Math.floor(Date.now() / 1000);
      setPolicies(updatedPolicies);
      
      setStatus("Heartbeat sent successfully!");
      setTimeout(() => setStatus(""), 3000);
    } catch (error) {
      const errorInfo = handleError(error, 'Heartbeat');
      setStatus(`Error: ${errorInfo.userMessage}`);
    }
  };

  // Refresh policy
  const refreshPolicy = async (policy, index) => {
    try {
      setStatus("Refreshing policy...");
      const updatedPolicyData = await contractService.getPolicyView(policy.policyId);
      
      const updated = {
        ...policy,
        ...updatedPolicyData,
        lastHeartbeat: Number(updatedPolicyData.lastHeartbeat),
        heartbeatInterval: Number(updatedPolicyData.heartbeatInterval),
        requiredQuorum: Number(updatedPolicyData.requiredQuorum),
        status: Number(updatedPolicyData.status),
        createdAt: Number(updatedPolicyData.createdAt)
      };
      
      const updatedList = [...policies];
      updatedList[index] = updated;
      setPolicies(updatedList);
      setStatus("Policy refreshed!");
      setTimeout(() => setStatus(""), 3000);
    } catch (error) {
      const errorInfo = handleError(error, 'Refresh Policy');
      setStatus(`Error: ${errorInfo.userMessage}`);
    }
  };

  const getStatusBadge = (code) => {
    switch (code) {
      case 1:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 flex items-center gap-1">
          <Activity className="w-3 h-3" /> Active
        </span>;
      case 2:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">Released</span>;
      case 3:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-800">Revoked</span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-800">Pending</span>;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "Never";
    const seconds = Math.floor(Date.now() / 1000 - timestamp);
    if (seconds < 60) return "Just now";
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getNextHeartbeat = (lastHeartbeat, interval) => {
    if (!lastHeartbeat) return "Not set";
    const nextTime = (lastHeartbeat + interval) * 1000;
    return new Date(nextTime).toLocaleString();
  };

  // Calculate stats
  const stats = {
    total: policies.length,
    active: policies.filter(p => p.status === 1).length,
    released: policies.filter(p => p.status === 2).length,
    revoked: policies.filter(p => p.status === 3).length
  };

  return (
    <>
      <ModernNavbar />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                <Shield className="w-8 h-8 text-indigo-600" />
                Your Policies
              </h1>
              <p className="text-gray-600">Manage your digital will policies</p>
            </div>
            <button
              onClick={onRegisterClick}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <Plus className="w-5 h-5" />
              Create New Policy
            </button>
          </div>

          {/* Connected Address */}
          <div className="mt-4 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Connected Wallet:</span>
              <span className="ml-2 font-mono text-indigo-600">{address}</span>
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-6 shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <Shield className="w-10 h-10 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 mb-1">Active</p>
                <p className="text-3xl font-bold text-green-700">{stats.active}</p>
              </div>
              <Activity className="w-10 h-10 text-green-400" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 mb-1">Released</p>
                <p className="text-3xl font-bold text-blue-700">{stats.released}</p>
              </div>
              <FileText className="w-10 h-10 text-blue-400" />
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md border border-red-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 mb-1">Revoked</p>
                <p className="text-3xl font-bold text-red-700">{stats.revoked}</p>
              </div>
              <Shield className="w-10 h-10 text-red-400" />
            </div>
          </div>
        </div>

        {/* Status Message */}
        {status && (
          <div className={`mb-6 p-4 rounded-xl ${
            status.includes('Error') ? 'bg-red-50 text-red-800 border border-red-200' :
            status.includes('success') ? 'bg-green-50 text-green-800 border border-green-200' :
            'bg-blue-50 text-blue-800 border border-blue-200'
          }`}>
            <p className="text-center font-medium">{status}</p>
          </div>
        )}

        {/* Policies List */}
        {loading ? (
          <div className="text-center py-20">
            <RefreshCw className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
            <p className="text-gray-600">Loading your policies...</p>
          </div>
        ) : policies.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center border border-gray-200">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Policies Yet</h3>
            <p className="text-gray-600 mb-6">Create your first digital will policy to get started</p>
            <button
              onClick={onRegisterClick}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
            >
              <Plus className="w-5 h-5" />
              Create Policy
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {policies.map((policy, index) => (
              <div
                key={policy.policyId}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        Policy #{index + 1}
                      </h3>
                      <p className="text-xs font-mono text-gray-500 break-all">
                        {policy.policyId}
                      </p>
                    </div>
                    {getStatusBadge(policy.status)}
                  </div>

                  {/* Info Grid */}
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    {/* Left Column */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <FileText className="w-5 h-5 text-indigo-600 mt-1 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-700 mb-1">Document CID</p>
                          <a
                            href={`https://gateway.pinata.cloud/ipfs/${policy.metadataCid || ''}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sm text-indigo-600 hover:text-indigo-800 flex items-center gap-1 break-all"
                          >
                            {policy.metadataCid ? policy.metadataCid.substring(0, 20) + '...' : 'N/A'}
                            <ExternalLink className="w-3 h-3 flex-shrink-0" />
                          </a>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Heart className="w-5 h-5 text-red-500 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">Last Heartbeat</p>
                          <p className="text-sm text-gray-600">
                            {formatTimeAgo(policy.lastHeartbeat)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {policy.lastHeartbeat ? new Date(policy.lastHeartbeat * 1000).toLocaleString() : "Never"}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Clock className="w-5 h-5 text-blue-600 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">Heartbeat Interval</p>
                          <p className="text-sm text-gray-600">
                            {Math.floor(policy.heartbeatInterval / 3600)} hours
                          </p>
                          <p className="text-xs text-gray-500">
                            Next: {getNextHeartbeat(policy.lastHeartbeat, policy.heartbeatInterval)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Users className="w-5 h-5 text-purple-600 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">Beneficiaries</p>
                          <p className="text-sm text-gray-600">
                            {policy.beneficiaries?.length || 0} beneficiaries (Quorum: {policy.requiredQuorum})
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3">
                        <Calendar className="w-5 h-5 text-green-600 mt-1" />
                        <div>
                          <p className="text-sm font-semibold text-gray-700 mb-1">Created</p>
                          <p className="text-sm text-gray-600">
                            {new Date(policy.createdAt * 1000).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Beneficiaries List */}
                  {policy.beneficiaries && policy.beneficiaries.length > 0 && (
                    <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                      <p className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                        <Users className="w-4 h-4" />
                        Beneficiary Addresses
                      </p>
                      <div className="space-y-1">
                        {policy.beneficiaries.map((beneficiary, idx) => (
                          <p key={idx} className="font-mono text-xs text-gray-600 break-all">
                            {idx + 1}. {beneficiary}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => sendHeartbeat(policy.policyId, index)}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-red-500 to-pink-500 text-white rounded-xl font-semibold shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                    >
                      <Heart className="w-5 h-5" />
                      Send Heartbeat
                    </button>
                    <button
                      onClick={() => refreshPolicy(policy, index)}
                      className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold shadow-md hover:shadow-lg hover:bg-gray-200 transition-all duration-200"
                    >
                      <RefreshCw className="w-5 h-5" />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </>
  );
}
